import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";

// Phase 1.6 — dark editorial imagery
const WORKSHOP_HERO =
  "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/f5e421529ffdefe35a6c5e08fae3ba0bab5606d51c8e723682fb21e26625caf9.png";
const HANDS_WIRING =
  "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/b1531dfa478a7e2b687c31f873dc5fc91b5b56a4b54b126046166b0a8ec6d774.png";
const GREENHOUSE =
  "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/bf1983370910825bb331594325c1ab1389c2cb6a5091e2ab84ef93d01211966c.png";

const PARAGRAPHS = [
  "I didn't come to this through a degree. I came to it through grief. After my grandfather died I inherited his small workshop, his cracked ceramic pots, and one juniper bonsai he'd been training for forty-one years. I almost killed it the first week. I learned how to keep it alive over the next two.",
  "What I figured out is that the plants weren't the point. The point was the patience they pulled out of me. Bonsai, succulents, terrariums — the whole quiet world of small living things — they teach you to slow down, to notice, to do one thing well. In a culture that sells speed, that's almost rebellious.",
  "Coastal Roots Botanica is the workbench I wish I'd had when I started. Hand-shaped trees. Honest tools. A community of people who'd rather spend a Sunday wiring a branch than scrolling through someone else's life. If that sounds like your kind of slow, you'd be welcome here.",
];

export default function About() {
  return (
    <PublicLayout overlayNav padTop={false}>
      {/* Editorial workshop hero — wide */}
      <section
        data-testid="about-workshop-hero"
        className="relative isolate flex min-h-[60vh] items-end overflow-hidden"
      >
        <div className="absolute inset-0 -z-10">
          <img
            src={WORKSHOP_HERO}
            alt="The workshop interior on the Pacific coast"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#3D3A33]/20 via-[#3D3A33]/55 to-[#3D3A33]" />
        </div>
        <div className="mx-auto w-full max-w-4xl px-6 pb-20 pt-32 text-[#F5EFE2]">
          <p className="text-xs uppercase tracking-[0.28em] text-[#C9A961]">
            About Kenny
          </p>
          <h1
            data-testid="about-headline"
            className="mt-4 font-serif text-5xl leading-[1.05] tracking-wide sm:text-6xl lg:text-7xl"
          >
            The Long Way of Growing.
          </h1>
          <p className="mt-4 max-w-xl text-base text-[#F5EFE2]/85 sm:text-lg">
            A salt-warped workshop, one juniper that wouldn&apos;t die, and the
            slowest decade of my life.
          </p>
        </div>
      </section>

      {/* THE ESSAY */}
      <article
        data-testid="about-page"
        className="mx-auto max-w-3xl px-6 py-20 text-[#F5EFE2]"
      >
        <div className="space-y-6 text-lg leading-relaxed text-[#F5EFE2]/90">
          <p data-testid="about-paragraph-0">{PARAGRAPHS[0]}</p>
          <p data-testid="about-paragraph-1">{PARAGRAPHS[1]}</p>
        </div>
      </article>

      {/* HANDS WIRING — mid-page editorial slab */}
      <section
        data-testid="about-hands-wiring"
        className="relative bg-[#3D3A33]"
      >
        <div className="relative mx-auto max-w-6xl px-6">
          <div className="overflow-hidden rounded-2xl border border-[#5E594E]">
            <img
              src={HANDS_WIRING}
              alt="Kenny's hands wiring a juniper branch in the workshop"
              className="aspect-[16/7] w-full object-cover"
              loading="lazy"
            />
          </div>
          <p className="mt-4 text-center font-serif text-base italic text-[#C9BEA8]">
            Wiring a juniper. Thirty seconds per bend. The tree decides the
            rest.
          </p>
        </div>
      </section>

      {/* CLOSING PARAGRAPH */}
      <article className="mx-auto max-w-3xl px-6 py-20 text-[#F5EFE2]">
        <div className="space-y-6 text-lg leading-relaxed text-[#F5EFE2]/90">
          <p data-testid="about-paragraph-2">{PARAGRAPHS[2]}</p>
          <p className="font-serif text-xl italic tracking-wide text-[#C9A961]">
            — Kenny
          </p>
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <Link to="/community">
            <Button
              className="bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
              data-testid="about-join-btn"
            >
              Join the community — $50/mo
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
          <Link to="/shop">
            <Button
              variant="outline"
              data-testid="about-shop-btn"
              className="border-[#C9A961]/70 bg-transparent text-[#C9A961] hover:bg-[#C9A961] hover:text-[#3D3A33]"
            >
              Browse the workbench
            </Button>
          </Link>
        </div>
      </article>

      {/* GREENHOUSE GOLDEN HOUR — closing image */}
      <section data-testid="about-greenhouse" className="bg-[#3D3A33] pb-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="overflow-hidden rounded-2xl border border-[#5E594E]">
            <img
              src={GREENHOUSE}
              alt="The greenhouse at golden hour"
              className="aspect-[16/9] w-full object-cover"
              loading="lazy"
            />
          </div>
          <p className="mt-4 text-center font-serif text-base italic text-[#C9BEA8]">
            The nursery. Open by appointment.
          </p>
        </div>
      </section>
    </PublicLayout>
  );
}
